import { Card, Colors, Heading, Icon, Text } from '@amelio/ui-library';
import { useMemo } from 'react';

import {
    COMPENSATION_CAPTION,
    COMPENSATION_OVERLINE,
    COMPENSATION_SECTION_TITLE,
    COMPENSATION_SPLIT_ROW,
    COMPENSATION_TILE_VALUE,
} from '../compensationStyles';
import { AdjustmentsTable } from './AdjustmentsTable';
import { AdjustmentsToolbar } from './AdjustmentsToolbar';
import { useAdjustmentsTable } from '../hooks/useAdjustmentsTable';

import type {
    LineLockChange,
    LineNoteChange,
    LinePercentChange,
    LineSalaryChange,
} from './AdjustmentsTable';
import type {
    CompensationComputedView,
    CompensationDraftLine,
    CompensationLevel,
    CompensationLine,
} from '@/types/compensation';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { formatCurrency } from '@/utils/numberFormat';

interface AdjustmentsSectionProps {
    computed: CompensationComputedView;
    draftLines: CompensationDraftLine[];
    levels: CompensationLevel[];
    canEdit: boolean;
    /** Whether the reader may change salaries, on top of `canEdit`. */
    canRecordSalaries: boolean;
    onSalaryChange: (change: LineSalaryChange) => void;
    onPercentChange: (change: LinePercentChange) => void;
    onLockChange: (change: LineLockChange) => void;
    onNoteChange: (change: LineNoteChange) => void;
    onExport: () => void;
    isExporting: boolean;
}

/**
 * Section 3: the decision itself, person by person. Its header is the one place that says whether
 * the envelope adds up — allocated against budget, the rounding residual, the people the
 * calculation had to leave out — before the table lets any of it be overridden by hand.
 */
export const AdjustmentsSection = ({
    computed,
    draftLines,
    levels,
    canEdit,
    canRecordSalaries,
    onSalaryChange,
    onPercentChange,
    onLockChange,
    onNoteChange,
    onExport,
    isExporting,
}: AdjustmentsSectionProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();

    const {
        search,
        setSearch,
        sort,
        toggleSort,
        page,
        setPage,
        totalPages,
        matchCount,
        pageLines,
    } = useAdjustmentsTable({ lines: computed.lines, language });

    const draftLinesById = useMemo(
        () => new Map(draftLines.map((line) => [line.employeeId, line])),
        [draftLines],
    );

    /** Locking freezes the amount on screen rather than the pre-rebalance proposal, so it never jumps. */
    const handleToggleLock = (line: CompensationLine) =>
        onLockChange({
            employeeId: line.employeeId,
            lockedAmount: draftLinesById.get(line.employeeId)?.isLocked ? null : line.amount,
        });

    const levelsById = useMemo(() => new Map(levels.map((level) => [level.id, level])), [levels]);

    const isOverrun = computed.overrun > 0;

    // The tile carries the verdict in its ground. The figure stays navy on red, where `ERROR_800`
    // would only reach 2.8:1.
    const allocatedTileClassName = isOverrun
        ? 'bg-error-50 rounded-[12px] p-[14px]'
        : 'bg-primary-50 rounded-[12px] p-[14px]';
    const allocatedValueColor = isOverrun ? Colors.NAVY : Colors.PRIMARY_800;

    const residualName =
        computed.residualEmployeeId === null
            ? null
            : (computed.lines.find((line) => line.employeeId === computed.residualEmployeeId)
                  ?.displayName ?? null);

    // Only the halves that have something to report: a healthy envelope announcing "0 employé sans
    // évaluation. 0 employé sans salaire connu." reads as two alarms under the section title.
    const missingDataNotice = [
        computed.withoutScoreCount > 0
            ? t('compensation.audience.withoutEvaluationCount', {
                  count: computed.withoutScoreCount,
              })
            : null,
        computed.withoutSalaryCount > 0
            ? t('compensation.audience.withoutSalaryCount', { count: computed.withoutSalaryCount })
            : null,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <Card
            // Opaque, where sections 1 and 2 are frosted: this one holds some forty inline white
            // inputs, and the page gradient showing through behind them tinted every field.
            bgColor={'bg-white'}
            borderRadius={'3xl'}
            dataTestId={'compensation-adjustments-section'}
        >
            <div className={'flex flex-col gap-[20px]'}>
                <div className={COMPENSATION_SPLIT_ROW}>
                    <div className={'flex flex-col gap-1'}>
                        <Heading
                            level={2}
                            color={Colors.BASE_500}
                            className={COMPENSATION_SECTION_TITLE}
                        >
                            {t('compensation.adjustments.title')}
                        </Heading>

                        <Text color={Colors.BASE_500}>
                            {t('compensation.adjustments.subtitle')}
                        </Text>
                    </div>

                    <div
                        className={`${allocatedTileClassName} flex flex-col gap-1`}
                        data-testid={'compensation-allocated'}
                    >
                        <Text
                            color={Colors.NAVY}
                            className={COMPENSATION_OVERLINE}
                        >
                            {t('compensation.adjustments.allocated')}
                        </Text>

                        <Text
                            color={allocatedValueColor}
                            className={`${COMPENSATION_TILE_VALUE} whitespace-nowrap tabular-nums`}
                        >
                            {`${formatCurrency(computed.allocated, language)} / ${formatCurrency(computed.budgetAmount, language)}`}
                        </Text>
                    </div>
                </div>

                {isOverrun ? (
                    <div
                        className={'bg-error-50 flex items-center gap-2 rounded-[12px] p-[12px]'}
                        data-testid={'compensation-overrun-banner'}
                    >
                        <Icon
                            name={'essential-warning-2'}
                            size={'md'}
                            color={Colors.ERROR}
                            className={'flex-shrink-0'}
                            ariaHidden
                        />

                        <Text color={Colors.NAVY}>
                            {t('compensation.adjustments.overrun', {
                                amount: formatCurrency(computed.overrun, language),
                            })}
                        </Text>
                    </div>
                ) : null}

                <div className={'flex flex-col gap-1'}>
                    {computed.residualAmount > 0 && residualName !== null ? (
                        <Text
                            color={Colors.BASE_500}
                            className={COMPENSATION_CAPTION}
                        >
                            {t('compensation.adjustments.residual', {
                                amount: formatCurrency(computed.residualAmount, language),
                                name: residualName,
                            })}
                        </Text>
                    ) : null}

                    {missingDataNotice === '' ? null : (
                        <Text
                            color={Colors.BASE_500}
                            className={COMPENSATION_CAPTION}
                        >
                            {missingDataNotice}
                        </Text>
                    )}
                </div>

                <AdjustmentsToolbar
                    search={search}
                    onSearchChange={setSearch}
                    onExport={onExport}
                    isExporting={isExporting}
                />

                <AdjustmentsTable
                    pageLines={pageLines}
                    draftLinesById={draftLinesById}
                    levelsById={levelsById}
                    sort={sort}
                    onToggleSort={toggleSort}
                    page={page}
                    totalPages={totalPages}
                    matchCount={matchCount}
                    onPageChange={setPage}
                    canEdit={canEdit}
                    canRecordSalaries={canRecordSalaries}
                    onSalaryChange={onSalaryChange}
                    onPercentChange={onPercentChange}
                    onToggleLock={handleToggleLock}
                    onNoteChange={onNoteChange}
                />
            </div>
        </Card>
    );
};
